import {LABEL_TYPES} from './model-item-enums'
import { Injectable } from '@angular/core';
import { Linqset, LinqsetNode, INSIGHTS_FIELD, NODE_SUPERTYPE, getInsightsWithoutCreate, getActionFrequencyTypeShortName, usesCalculatedFrequency, EDGE_TYPE, NODE_TYPE, isSupplyChain, getInsightsForNode, CURRENT_STATE_PROJECT_KEY } from '../common/linqset-model';
import { FormattingService } from '../linq-utils/formatting/formatting.service';
import { InformationAsset, ModelDetails, TagGroup } from '../common/organisation';
import { TagsService } from '../organisation/tags/tags-service';
import { NodeService } from './node.service';
import { getArrowIcon } from '../linq-utils/image-constants';
import { InsightsFieldType } from '../insights/model/insights-field-type';
import { NODE_TYPE_DATA_TYPE } from '../insights/node-types/node-type-data';
import { AdjacencyTypes, Color, ExteriorNodeLabelModel, IconLabelStyle, IEdge, IGraph, ILabelModelParameter, INode, InteriorNodeLabelModel, LabelStyle, Size } from '@yfiles/yfiles';
import { LAYOUT_DIRECTION } from '../common/user';

@Injectable()
export class LabelService {
    nodeInsightsField:INSIGHTS_FIELD = INSIGHTS_FIELD.ANNUAL_HOURS
    defaultStyle:LabelStyle
    tagGroup:TagGroup

    constructor(private formattingService:FormattingService, private tagsService:TagsService, private nodeService:NodeService) {
        this.defaultStyle = new LabelStyle({font:"12px Segoe UI, Segoe, Tahoma, Helvetica, Arial, sans-serif", backgroundFill:Color.WHITE})
	}

    removeLabels(graph:IGraph, includeNames:boolean=false) {
        if (includeNames) {
            graph.labels.toList().forEach(l => {
                graph.remove(l)
            })
        }else {
            graph.labels.toList().filter(l => l.tag && l.tag.type !== LABEL_TYPES.NODE_NAME).forEach(l => {
                graph.remove(l)
            })
        }
    }

    updateLabelsForGraph(graph: IGraph, updateStyles:boolean, linqset?:Linqset, field?:INSIGHTS_FIELD) {
        this.updateLabelsForNodes(graph, updateStyles, linqset, field)
        this.updateAllEdges(graph, this.nodeInsightsField)
    }

    private updateLabelsForNodes(graph: IGraph, updateStyles:boolean, linqset:Linqset, field?:INSIGHTS_FIELD) {
        if (field) this.nodeInsightsField = field

        const fieldType = new InsightsFieldType(this.nodeInsightsField, linqset.modelKey, linqset.workspaceKey)
        linqset.maxNodeValue = 0
        linqset.maxEdgeValue = 0

        graph.nodes.filter(n => n.tag).forEach(n => {
            this.updateLabelsForNode(graph, n, linqset, fieldType)
            if (updateStyles && n.tag?.node) this.nodeService.setNodeStyle(graph, n, linqset)
        })
        if (!graph.tag) graph.tag ={}
        graph.tag.maxNodeValue = linqset.maxNodeValue
        graph.tag.maxEdgeValue = linqset.maxEdgeValue
    }

    private updateAllEdges(graph:IGraph, field?:INSIGHTS_FIELD) {
        if (field && field === INSIGHTS_FIELD.PROCESSED) {
            graph.edges.filter(e => (e.tag?.edge?.type === EDGE_TYPE.CREATE || e.tag?.edge?.type === EDGE_TYPE.SUPPORT || e.tag?.edge?.type === EDGE_TYPE.JOINS)).forEach(e => {
                // a share other than all of them is shown, 0% included
                const share = e.tag.edge.outputPercentage
                if (share !== undefined && share !== null && share !== 100)
                    this.setLabelOnEdge(graph, e, e.tag.edge.outputPercentage + '%')
                else this.setLabelOnEdge(graph, e, '')
            })
        }else {
            graph.edges.filter(e => e.tag?.label).forEach(e => {
                this.setLabelOnEdge(graph, e, e.tag.label)
            })
        }

    }

    setLabelOnEdge(graph:IGraph, edge:IEdge, label:string) {
        // have to do this so dont get a collections changed exception when removing
        const list = edge.labels.map(l => l).toArray()
        let existingLabel = false
        list.forEach(l => {
            if (label && label === l.text) existingLabel = true
            else graph.remove(l)
        })

        if (label && label.length > 0 && !existingLabel) {
            graph.addLabel(edge, label)
        }
    }

    removeLabelsExceptName(graph: IGraph) {
        graph.nodes.forEach(node => {
            // have to do this so dont get a collections changed exception when removing
            const labels = node.labels?node.labels.filter(l => l.tag && l.tag.type !== LABEL_TYPES.NODE_NAME).map(l => {return l}).toList():[]

            labels.forEach(l => {
                try {
                    graph.remove(l)
                }catch(err) {
                    console.log("Cannot remove label: " + err.toString())
                }
            })
        })

        graph.edges.filter(edge => edge.labels?.size > 0).forEach(edge => {
            const list = edge.labels.map(l => l).toArray()
            list.forEach(l => graph.remove(l))
        })
    }

    private getFormattedFieldValue(fieldType:InsightsFieldType, insights:any, dataType:NODE_TYPE_DATA_TYPE):{raw:number, formatted:string} {
        const raw = insights?fieldType.getSingleValueFromInsights(insights, dataType):0
        const formatted = fieldType.isCurrency()?this.formattingService.formatCost(raw):this.formattingService.formatNumber(raw)
        return {raw, formatted}
    }

    updateLabelsForNode(graph:IGraph, node:INode, linqset?:Linqset, fieldType?:InsightsFieldType) {
        if (!node.tag) return

        if (!fieldType) {
            fieldType = new InsightsFieldType(this.nodeInsightsField, linqset.modelKey, linqset.workspaceKey)
        }

        if (fieldType.type === INSIGHTS_FIELD.NONE || node.tag?.excluded) {
            this.removeLabelForNode(graph, node, LABEL_TYPES.COSTS)
        }
        let tagIcon:string = undefined

        node.tag.value = 0

        if (node.tag?.asset) {
            const asset = node.tag.asset as InformationAsset
            if (asset.name && asset.name.length > 0)
                this.addLabelToNode(graph, node, asset.name, LABEL_TYPES.NODE_NAME)

        }else if (node.tag?.model) {
            // model node
            const model = node.tag.model as ModelDetails
            const insights = linqset && model.insights?.find(i => i.projectKey === linqset.workspaceKey)

            if (this.tagGroup && model.tagKeys && model.tagKeys.length > 0) {
                const tag = this.tagGroup.tags.find(t => t._key === model.tagKeys[0])
                if (tag) tagIcon = this.tagsService.getIconForTag(tag)
            }
            this.addLabelToNode(graph, node, model.name, LABEL_TYPES.NODE_NAME, tagIcon)

            if (insights && fieldType.type !== INSIGHTS_FIELD.NONE) {
                const v = this.getFormattedFieldValue(fieldType, insights, NODE_TYPE_DATA_TYPE.MODELS)
                this.addLabelToNode(graph, node, v.formatted, LABEL_TYPES.COSTS)
                node.tag.value = v.raw

            }
        }else if (node.tag && node.tag.group) {
            if (node.tag.group.tag) {
                tagIcon = this.tagsService.getIconForTag(node.tag.group.tag)
            }else if (this.tagGroup && node.tag.group.action) {
                const tag = node.tag.group.action.properties.tagKeys[this.tagGroup._key] && this.tagGroup.tags.find(t => t._key === node.tag.group.action.properties.tagKeys[this.tagGroup._key])
                if (tag) tagIcon = this.tagsService.getIconForTag(tag)
            }
            // added once and kept up to date: refreshing labels used to add another copy each time
            const groupName = node.tag.group.name || ''
            // a group made with its name as a plain label (as the group service does) has that label adopted
            const nameLabel = node.labels.find(l => l.tag?.type === LABEL_TYPES.GROUP_NAME) || node.labels.find(l => !l.tag)
            if (nameLabel && !nameLabel.tag) nameLabel.tag = {type: LABEL_TYPES.GROUP_NAME}
            if (!nameLabel) {
                graph.addLabel({owner:node, text:groupName,
                    layoutParameter:graph.groupNodeDefaults.labels.layoutParameter,
                    style: graph.groupNodeDefaults.labels.style,
                    tag:{
                        type: LABEL_TYPES.GROUP_NAME
                }});
            }else if (nameLabel.text !== groupName) {
                graph.setLabelText(nameLabel, groupName)
            }

            if (node.tag.group.insights) {
                const v = this.getFormattedFieldValue(fieldType, node.tag.group.insights, NODE_TYPE_DATA_TYPE.GROUP)
                this.addLabelToNode(graph, node, v.formatted, LABEL_TYPES.COSTS)
                node.tag.value = v.raw
            }
        }else if (node.tag?.node) {
            const linqsetNode = node.tag.node as LinqsetNode
            const insights = linqset && getInsightsWithoutCreate(linqsetNode, linqset)

            const dataType = fieldType.outcomeApplies() && ((linqsetNode.superType === NODE_SUPERTYPE.INFORMATION && linqsetNode.modelKey === linqset.modelKey) || linqsetNode.superType === NODE_SUPERTYPE.OUTCOMES)?NODE_TYPE_DATA_TYPE.OUTPUT:NODE_TYPE_DATA_TYPE.STANDARD

            if (fieldType.type !== INSIGHTS_FIELD.NONE && fieldType.doesFieldTypeApply(linqsetNode.superType, dataType)) {
                const v = (insights && this.getFormattedFieldValue(fieldType, insights, dataType))||{raw:0, formatted:'0'}
                node.tag.value = v.raw

                let label = (dataType === NODE_TYPE_DATA_TYPE.OUTPUT)?"Total: " + v.formatted:v.formatted

                let icon = undefined
                if (linqset.isProjectModel()) {
                    const csInsights = getInsightsForNode(linqsetNode, linqset.modelKey, CURRENT_STATE_PROJECT_KEY)
                    const csValue = (csInsights && fieldType.getSingleValueFromInsights(csInsights, dataType))||0
                    icon = getArrowIcon(v.raw, csValue, true)
                }

                if (fieldType.type === INSIGHTS_FIELD.PROCESSED && linqsetNode.superType === NODE_SUPERTYPE.ACTION) {
                    if (usesCalculatedFrequency(linqsetNode.properties)) {
                        const edges = graph.edgesAt(node, AdjacencyTypes.INCOMING)
                        if (edges.filter(e => e.tag?.edge?.type === EDGE_TYPE.SUPPORT).size > 1)
                            label = label + ' | ' + getActionFrequencyTypeShortName(linqsetNode.properties?.actionProcessType)
                    }else {
                        label = linqsetNode.type === NODE_TYPE.CAPTURE?label + ' capture':label + '! none'
                    }
                }
                this.addLabelToNode(graph, node, label, LABEL_TYPES.COSTS, icon)
            }else {
                this.removeLabelForNode(graph, node, LABEL_TYPES.COSTS)
            }

            if (linqsetNode.superType === NODE_SUPERTYPE.ACTION && this.tagGroup && node.tag.node.properties) {
                const tag = node.tag.node.properties.tagKeys && node.tag.node.properties.tagKeys[this.tagGroup._key] && this.tagGroup.tags.find(t => t._key === node.tag.node.properties.tagKeys[this.tagGroup._key])
                if (tag) {
                    tagIcon = this.tagsService.getIconForTag(tag)
                }else {
                    this.removeLabelForNode(graph, node, LABEL_TYPES.TAG)
                }
            }
            this.addLabelToNode(graph, node, linqsetNode.name, LABEL_TYPES.NODE_NAME, tagIcon)
        }
        if (node?.tag?.node && isSupplyChain(node?.tag?.node?.superType)) {
            if (fieldType.type === INSIGHTS_FIELD.WORKLOAD) {
                if (linqset.maxNodeValue > node.tag.value) linqset.maxNodeValue = node.tag.value
            }else {
                if (linqset.maxNodeValue < node.tag.value) linqset.maxNodeValue = node.tag.value
            }

            graph.edgesAt(node, AdjacencyTypes.OUTGOING).forEach(e => {
                if (e.tag?.edge) {
                    // a missing percentage means all items, as in model processing; a missing proportion (processing sets it) means none, as before
                    const share = this.nodeInsightsField === INSIGHTS_FIELD.PROCESSED ? (e.tag.edge.outputPercentage ?? 100) / 100 : (e.tag.edge.proportion ?? 0)
                    e.tag.value = (Number(node.tag.value) || 0) * (Number(share) || 0)
                    if (fieldType.type === INSIGHTS_FIELD.WORKLOAD) {
                        if (linqset.maxEdgeValue > e.tag.value) linqset.maxEdgeValue = e.tag.value
                    }else {
                        if (linqset.maxEdgeValue < e.tag.value) linqset.maxEdgeValue = e.tag.value
                    }
                }
            })
        }

    }

    removeLabelForNode(graph:IGraph, node:INode, type:LABEL_TYPES) {
        const label = node.labels.find(l => l.tag && l.tag.type === type)
        if (label) {
            graph.remove(label)
        }
    }

    addLabelToNode(graph:IGraph, node:INode, text:string, type:LABEL_TYPES, icon?:string) {
        const label = node.labels.find(l => l.tag && l.tag.type === type)
        if (label) {
            if (label.text === text) {
                if (icon && label.style instanceof IconLabelStyle && label.style.href === icon) return label
                if (!icon && !(label.style instanceof IconLabelStyle)) return label
            }
            graph.remove(label)
        }
        if ((text && text.length > 0) || icon) {

            const orientation = graph.tag?.orientation || LAYOUT_DIRECTION.DOWN

            const layoutParam:ILabelModelParameter = this.getPlacementForType(type, orientation)
            const style = icon?new IconLabelStyle({
                href: icon,
                backgroundFill:Color.WHITE,
                iconSize: new Size(20, 20),
                iconPlacement: ExteriorNodeLabelModel.LEFT
            }):this.defaultStyle

            return graph.addLabel({owner:node, text:text, layoutParameter: layoutParam, style:style, tag:{
                type: type
            }});
        }
        return label
    }

    replaceLabelForLinqsetNode(graph:IGraph, node: LinqsetNode, text:string, type:LABEL_TYPES) {
        graph.nodes.filter(n => n.tag.node && n.tag.node._key === node._key).forEach(n => {
            this.addLabelToNode(graph, n, text, type)
        })
    }

    private getPlacementForType(type:LABEL_TYPES, orientation:LAYOUT_DIRECTION):ILabelModelParameter {
        switch (type) {
            default:
            case LABEL_TYPES.COSTS:
                return (orientation===LAYOUT_DIRECTION.LEFT_RIGHT||orientation===LAYOUT_DIRECTION.RIGHT_LEFT)?ExteriorNodeLabelModel.TOP:ExteriorNodeLabelModel.LEFT;
            case LABEL_TYPES.VALUE:
            case LABEL_TYPES.QUEUE:
                return orientation===LAYOUT_DIRECTION.LEFT_RIGHT?ExteriorNodeLabelModel.RIGHT:(orientation===LAYOUT_DIRECTION.UP?ExteriorNodeLabelModel.BOTTOM_RIGHT:ExteriorNodeLabelModel.TOP_RIGHT);
            case LABEL_TYPES.NODE_NAME:
                return orientation===LAYOUT_DIRECTION.UP?ExteriorNodeLabelModel.TOP:ExteriorNodeLabelModel.BOTTOM;
            case LABEL_TYPES.GROUP_NAME:
                return InteriorNodeLabelModel.TOP_LEFT
            case LABEL_TYPES.TAG:
                return ExteriorNodeLabelModel.RIGHT;
        }
    }

}